import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Lock, LockOpen } from "@phosphor-icons/react";
import { useSalaryStatus } from "./salary/SalaryVisibility";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** True zodra de gebruiker loongegevens heeft ontgrendeld (standaard verborgen). */
export const useLoonZichtbaar = () => useSalaryStatus().visible;

export const LoonSlot = () => {
  const { status, refresh: laad, invalidate } = useSalaryStatus();
  const [open, setOpen] = useState(false);
  const [wachtwoord, setWachtwoord] = useState("");
  const [bezig, setBezig] = useState(false);

  if (!status?.vereist) return null;

  const ontgrendel = async () => {
    setBezig(true);
    try {
      const res = await axios.post(`${API}/auth/loon-ontgrendel`, { wachtwoord });
      toast.success(`Loongegevens zichtbaar voor ${res.data.minuten} minuten`);
      setWachtwoord("");
      setOpen(false);
      await laad();
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ontgrendelen mislukt");
    } finally { setBezig(false); }
  };

  const vergrendel = async () => {
    invalidate();
    try {
      await axios.post(`${API}/auth/loon-vergrendel`);
      toast.success("Loongegevens weer verborgen");
      await laad();
      window.location.reload();
    } catch { toast.error("Vergrendelen mislukt"); }
  };

  return (
    <>
      {status.ontgrendeld ? (
        <Button
          size="sm"
          variant="outline"
          className="rounded-sm h-9 text-[#1B5E20] border-[#1B5E20]/40"
          onClick={vergrendel}
          data-testid="loon-vergrendel-btn"
        >
          <LockOpen size={15} weight="bold" className="mr-1.5" />
          Lonen zichtbaar{status.tot ? ` tot ${new Date(status.tot).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}` : ""}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="rounded-sm h-9"
          onClick={() => setOpen(true)}
          data-testid="loon-ontgrendel-btn"
        >
          <Lock size={15} weight="bold" className="mr-1.5" /> Lonen verborgen
        </Button>
      )}

      <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) setWachtwoord(""); }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="loon-unlock-dialog">
          <DialogHeader>
            <DialogTitle data-testid="loon-unlock-title">Loongegevens tonen</DialogTitle>
            <DialogDescription data-testid="loon-unlock-description">
              Geef je eigen wachtwoord opnieuw om uurlonen en loonkosten te zien.
              Ze blijven {status.minuten} minuten zichtbaar en zijn na elke nieuwe aanmelding opnieuw verborgen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); ontgrendel(); }}>
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="Jouw wachtwoord"
              value={wachtwoord}
              onChange={(e) => setWachtwoord(e.target.value)}
              className="rounded-sm"
              data-testid="loon-wachtwoord-input"
            />
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setOpen(false); setWachtwoord(""); }} data-testid="loon-annuleer-btn">
                Annuleren
              </Button>
              <Button
                type="submit"
                className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
                disabled={bezig || !wachtwoord}
                data-testid="loon-bevestig-btn"
              >
                {bezig ? "Controleren..." : "Tonen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
